import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import ActionButton from './ActionButton';
import Analytics from '../utils/Analytics';
import Environment from '../utils/Environment';
import {PAYWALL_EVENTS, PaywallSlug} from '../analytics/paywallEvents';
import {getActivePaywall} from '../analytics/activePaywall';
import {isLinkOutVisit} from '../utils/linkOutSession';
import {getStripeLoadState, retryStripeLoad, subscribeToStripeLoad} from '../utils/stripeLoader';

/** Where "Continue in the app" goes: the same bare scheme link the paywalls' prepaid-card block uses. */
export const APP_RETURN_URL = 'nyx://';

/** Pages that take payment from people who may not have the app, so never offer to go back to it. */
const NO_APP_RETURN: (PaywallSlug | null)[] = ['email-campaign', 'gift-purchase', 'gift-redeem', 'gift-success'];

const useStripeLoadState = () => {
  const [loadState, setLoadState] = useState(getStripeLoadState);
  useEffect(() => {
    setLoadState(getStripeLoadState());
    return subscribeToStripeLoad(() => setLoadState(getStripeLoadState()));
  }, []);
  return loadState;
};

/**
 * Shown over the bottom of the page, where the payment form would be, when Stripe.js can't load even after the
 * loader's automatic retry (see src/utils/stripeLoader.ts). Mounted once for every route, so no paywall needs its
 * own copy, and renders nothing unless the page on screen is the one whose Stripe load failed. Try again loads
 * Stripe.js afresh; on success the page's own payment UI appears and this goes away. Visits the Rise app opened also
 * get Continue in the app.
 */
const StripeLoadFallback: React.FC = () => {
  const {t} = useTranslation();
  const {pathname} = useLocation();
  const {status, path, attempts} = useStripeLoadState();

  if ((status !== 'failed' && status !== 'retrying') || path !== pathname) {
    return null;
  }

  const paywall = getActivePaywall();
  const linkOut = isLinkOutVisit();
  const showAppReturn = linkOut && !NO_APP_RETURN.includes(paywall);
  const retrying = status === 'retrying';

  const track = (props: Record<string, unknown>) =>
    Analytics.track(PAYWALL_EVENTS.stripeLoadFallbackAction, {
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      paywall,
      path: pathname,
      linkOut,
      ...props,
    });

  const onTryAgain = async () => {
    const recovered = await retryStripeLoad();
    track({action: 'try_again', recovered, attempt: attempts + 1});
  };

  return (
    <div
      role="alert"
      data-testid="stripe-load-fallback"
      className="fixed inset-x-0 bottom-0 flex justify-center bg-panelGray-1 border-t border-gray-1 rounded-t-2xl text-white text-center"
      style={{
        // Above BottomSheet (1000) and Modal (1001): a paywall that opens its plan sheet straight away would
        // otherwise cover the only way to pay.
        zIndex: 1100,
        padding: '24px 20px calc(24px + env(safe-area-inset-bottom))',
        boxShadow: '0 -8px 24px rgba(0,0,0,0.5)',
      }}
    >
      <div className="w-full max-w-sm">
        <h2 className="text-lg font-bold">{t('stripe_load_failed.title')}</h2>
        <p className="text-sm mt-2 mb-5 opacity-80">{t('stripe_load_failed.body')}</p>
        <ActionButton
          title={retrying ? t('stripe_load_failed.retrying') : t('stripe_load_failed.try_again')}
          onClick={onTryAgain}
          disabled={retrying}
        />
        {showAppReturn ? (
          <a
            href={APP_RETURN_URL}
            onClick={() => track({action: 'return_to_app'})}
            className="block w-full mt-3 p-4 rounded-md border border-gray-2 text-md tracking-widest text-white"
          >
            {t('stripe_load_failed.continue_in_app')}
          </a>
        ) : null}
      </div>
    </div>
  );
};

export default StripeLoadFallback;
